import React, { useRef, useState } from 'react';
import { useFeatureValue } from '@growthbook/growthbook-react';
import WithVisibilityObserver from '@gumtree/shared/src/util/element-visibility/visibility-observer';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { getLinkHostname } from '@gumtree/shared/src/util/ga4-bff';
import { ExperimentVariant, GrowthBookFeature } from '@gumtree/shared/src/model/experiment';

import { Tab, Tabs } from '@gumtree/ui-library';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import TopLocations from './top-locations';
import TopSearches from './top-searches';
import TopSearchCondensed from './top-search-condensed';
import ErrorBoundary from '../error-boundary';

import {
    BrowseLinksContainer,
    DesktopBrowseLinksContainer,
    VipBrowseLinksContainer,
} from './style';
import { getClinetPageCategories } from '../utils';

const BrowseLinksContent = ({
    deviceType,
    pageType = 'Homepage',
    browseLinks = null,
    isVotRedesign = false,
    isDesktopRedesign = false,
    isModernMobileTopLocations = false,
    mhcVariant = ExperimentVariant.VARIANT_A,
    whcVariant = ExperimentVariant.VARIANT_A,
}: BrowseLinksProps & {
    isDesktopRedesign?: boolean;
    isModernMobileTopLocations?: boolean;
    mhcVariant?: ExperimentVariant;
    whcVariant?: ExperimentVariant;
}) => {
    const hasShowLocation = useRef(false);

    if (!browseLinks) return null;

    const { topLocations, topCities, topSearches, topSearchCondensed } = browseLinks;

    const locationItems = pageType === 'Homepage' ? topLocations : topCities;

    const showRenderTopLocation =
        Array.isArray(locationItems) && locationItems.some((location) => location.items.length);

    const showModernBrowseLinks =
        deviceType === 'mobile' &&
        pageType === 'Homepage' &&
        (mhcVariant === ExperimentVariant.VARIANT_B || mhcVariant === ExperimentVariant.VARIANT_C);
    const showDesktopHomeRedesign =
        deviceType === 'desktop' &&
        pageType === 'Homepage' &&
        whcVariant === ExperimentVariant.VARIANT_C;

    const showTopSearch = () => {
        const listLinkDetails =
            (topSearches
                ? topSearches
                      .reduce((pre, next) => pre.concat(next.sections), [])
                      .reduce((pre, next) => pre.concat(next.items), [])
                : topSearchCondensed.reduce((pre, next) => pre.concat(next.items), [])) || [];
        trackGA4Event<GA4.BrowseLinksEvent>({
            event: 'link_impress',
            linkLocation: 'topSearches',
            linkType: 'inside',
            linkURL: listLinkDetails.map((i) => i.url).join(','),
            linkText: listLinkDetails.map((i) => i.name || i.label).join(','),
            linkDomain: listLinkDetails.map((i) => getLinkHostname(i.url)).join(','),
            linkDetail: {},
            listLinkDetail: getClinetPageCategories(),
        });
    };

    const showLocation = () => {
        if (hasShowLocation.current) return;
        const listLinkDetails = (locationItems || []).reduce(
            (pre, next) => pre.concat(next.items),
            []
        );
        trackGA4Event<GA4.BrowseLinksEvent>({
            event: 'link_impress',
            linkLocation: 'topLocations',
            linkType: 'inside',
            linkURL: listLinkDetails.map((i) => i.url).join(','),
            linkText: listLinkDetails.map((i) => i.name).join(','),
            linkDomain: listLinkDetails.map((i) => getLinkHostname(i.url)).join(','),
            linkDetail: {},
            listLinkDetail: getClinetPageCategories(),
        });
        hasShowLocation.current = true;
    };

    const onVisible = () => {
        const hasTopSearches = !!topSearches || !!topSearchCondensed;
        hasTopSearches ? showTopSearch() : showRenderTopLocation && showLocation();
    };

    const renderTopSearchTab = () => {
        if (topSearches) {
            return (
                <Tab title="Top searches" forceRender>
                    <TopSearches items={topSearches} />
                </Tab>
            );
        }
        if (topSearchCondensed) {
            return (
                <Tab title={topSearchCondensed[0].title} forceRender>
                    <TopSearchCondensed topSearchCondensed={topSearchCondensed} />
                </Tab>
            );
        }
        return null;
    };

    const renderVotTopSearchContent = () => {
        if (topSearches) {
            return <TopSearches items={topSearches} vot />;
        }
        if (topSearchCondensed) {
            return <TopSearchCondensed topSearchCondensed={topSearchCondensed} vot />;
        }
        return null;
    };

    const hasTopSearches = Boolean(topSearches || topSearchCondensed);
    const hasSingleModernMobileTopLocationsTab =
        isModernMobileTopLocations && showRenderTopLocation && !hasTopSearches;

    if (showModernBrowseLinks) {
        return (
            <ErrorBoundary>
                <div {...qaAttribute('browse-links-container')}>
                    {showRenderTopLocation && (
                        <WithVisibilityObserver onVisible={showLocation}>
                            <TopLocations items={locationItems} showTitle={false} modern />
                        </WithVisibilityObserver>
                    )}
                    {topSearches ? (
                        <WithVisibilityObserver onVisible={showTopSearch}>
                            <TopSearches items={topSearches} modern />
                        </WithVisibilityObserver>
                    ) : topSearchCondensed ? (
                        <WithVisibilityObserver onVisible={showTopSearch}>
                            <TopSearchCondensed topSearchCondensed={topSearchCondensed} modern />
                        </WithVisibilityObserver>
                    ) : null}
                </div>
            </ErrorBoundary>
        );
    }

    if (hasSingleModernMobileTopLocationsTab) {
        return (
            <ErrorBoundary>
                <WithVisibilityObserver onVisible={showLocation}>
                    <div {...qaAttribute('browse-links-container')}>
                        <TopLocations items={locationItems} showTitle={false} modern />
                    </div>
                </WithVisibilityObserver>
            </ErrorBoundary>
        );
    }

    if (showDesktopHomeRedesign || isDesktopRedesign) {
        return (
            <ErrorBoundary>
                <DesktopBrowseLinks
                    hasTopSearches={hasTopSearches}
                    locationItems={locationItems}
                    renderTopSearchContent={renderVotTopSearchContent}
                    showLocation={showLocation}
                    showTopSearch={showTopSearch}
                    showTopLocations={showRenderTopLocation}
                />
            </ErrorBoundary>
        );
    }

    if (isVotRedesign) {
        return (
            <ErrorBoundary>
                <VotVipBrowseLinks
                    hasTopSearches={hasTopSearches}
                    locationItems={locationItems}
                    modernTopLocations={isModernMobileTopLocations}
                    renderTopSearchContent={renderVotTopSearchContent}
                    showLocation={showLocation}
                    showTopSearch={showTopSearch}
                    showTopLocations={showRenderTopLocation}
                />
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary>
            <WithVisibilityObserver onVisible={onVisible}>
                <BrowseLinksContainer
                    className={
                        hasSingleModernMobileTopLocationsTab
                            ? 'browse-links-container--rh-mobile-single-location'
                            : undefined
                    }
                    isSection
                    {...qaAttribute('browse-links-container')}
                >
                    <Tabs>
                        {renderTopSearchTab()}
                        {showRenderTopLocation && (
                            <Tab
                                title="Top Locations"
                                {...qaAttribute('top-locations-toggler')}
                                onClick={showLocation}
                                forceRender
                            >
                                <TopLocations
                                    items={locationItems}
                                    modern={isModernMobileTopLocations}
                                    showModernTitle={!isModernMobileTopLocations}
                                    showTitle={false}
                                    {...qaAttribute('top-locations-content')}
                                />
                            </Tab>
                        )}
                    </Tabs>
                </BrowseLinksContainer>
            </WithVisibilityObserver>
        </ErrorBoundary>
    );
};

const DesktopBrowseLinks = ({
    hasTopSearches,
    locationItems,
    renderTopSearchContent,
    showLocation,
    showTopSearch,
    showTopLocations,
}: VotVipBrowseLinksProps) => {
    const tabs = [
        ...(showTopLocations ? [{ id: 'locations', label: 'Top Locations' }] : []),
        ...(hasTopSearches ? [{ id: 'searches', label: 'Top Searches' }] : []),
    ];
    const [activeTab, setActiveTab] = useState(tabs[0]?.id);
    const hasMultipleTabs = tabs.length > 1;

    if (!tabs.length) return null;

    return (
        <WithVisibilityObserver
            onVisible={() => (activeTab === 'locations' ? showLocation() : showTopSearch())}
        >
            <DesktopBrowseLinksContainer isSection {...qaAttribute('browse-links-container')}>
                {hasMultipleTabs ? (
                    <div className="desktop-browse-links__tabs" role="tablist">
                        {tabs.map((tab) => {
                            const isActive = activeTab === tab.id;

                            return (
                                <button
                                    aria-controls={`desktop-browse-links-${tab.id}`}
                                    aria-selected={isActive}
                                    className="desktop-browse-links__tab"
                                    key={tab.id}
                                    onClick={() => {
                                        setActiveTab(tab.id);
                                        if (tab.id === 'locations') {
                                            showLocation();
                                        } else {
                                            showTopSearch();
                                        }
                                    }}
                                    role="tab"
                                    type="button"
                                >
                                    <span>{tab.label}</span>
                                    {isActive && <span className="desktop-browse-links__marker" />}
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <h2 className="desktop-browse-links__single-title">{tabs[0].label}</h2>
                )}
                <div
                    className="desktop-browse-links__tabpanel"
                    id={`desktop-browse-links-${activeTab}`}
                    role="tabpanel"
                >
                    {activeTab === 'locations' ? (
                        <TopLocations
                            desktopRedesign
                            items={locationItems}
                            showDesktopTitle={false}
                            showTitle={false}
                            {...qaAttribute('top-locations-content')}
                        />
                    ) : (
                        renderTopSearchContent()
                    )}
                </div>
            </DesktopBrowseLinksContainer>
        </WithVisibilityObserver>
    );
};

const VotVipBrowseLinks = ({
    hasTopSearches,
    locationItems,
    modernTopLocations = false,
    renderTopSearchContent,
    showLocation,
    showTopSearch,
    showTopLocations,
}: VotVipBrowseLinksProps) => {
    const tabs = [
        ...(showTopLocations ? [{ id: 'locations', label: 'Top Locations' }] : []),
        ...(hasTopSearches ? [{ id: 'searches', label: 'Top Searches' }] : []),
    ];
    const [activeTab, setActiveTab] = useState(tabs[0]?.id);
    const hasMultipleTabs = tabs.length > 1;

    if (!tabs.length) return null;

    return (
        <WithVisibilityObserver
            onVisible={() => (activeTab === 'locations' ? showLocation() : showTopSearch())}
        >
            <VipBrowseLinksContainer
                isSection
                data-vot-tab-count={tabs.length}
                {...qaAttribute('browse-links-container')}
            >
                <div className="vip-vot-browse-links__tabs" role="tablist">
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab.id;

                        return (
                            <button
                                aria-controls={`vip-vot-browse-links-${tab.id}`}
                                aria-selected={isActive}
                                className="vip-vot-browse-links__tab"
                                key={tab.id}
                                onClick={() => {
                                    setActiveTab(tab.id);
                                    if (tab.id === 'locations') {
                                        showLocation();
                                    } else {
                                        showTopSearch();
                                    }
                                }}
                                role="tab"
                                type="button"
                            >
                                <span>{tab.label}</span>
                                {hasMultipleTabs && isActive && (
                                    <span className="vip-vot-browse-links__marker" />
                                )}
                            </button>
                        );
                    })}
                </div>
                <div
                    className="vip-vot-browse-links__tabpanel"
                    id={`vip-vot-browse-links-${activeTab}`}
                    role="tabpanel"
                >
                    {activeTab === 'locations' ? (
                        <TopLocations
                            items={locationItems}
                            modern={modernTopLocations}
                            showModernTitle={!modernTopLocations}
                            showTitle={false}
                            vot={!modernTopLocations}
                            {...qaAttribute('top-locations-content')}
                        />
                    ) : (
                        renderTopSearchContent()
                    )}
                </div>
            </VipBrowseLinksContainer>
        </WithVisibilityObserver>
    );
};

const MobileHomepageBrowseLinks = (props: BrowseLinksProps) => {
    const mhcVariant = useFeatureValue(
        GrowthBookFeature.MHC,
        ExperimentVariant.VARIANT_A
    ) as ExperimentVariant;
    return <BrowseLinksContent {...props} mhcVariant={mhcVariant} />;
};

const DesktopHomepageBrowseLinks = (props: BrowseLinksProps) => {
    const whcVariant = useFeatureValue(
        GrowthBookFeature.WHC,
        ExperimentVariant.VARIANT_A
    ) as ExperimentVariant;
    return <BrowseLinksContent {...props} whcVariant={whcVariant} />;
};

const VipBrowseLinks = (props: BrowseLinksProps) => {
    const variant = useFeatureValue(
        props.deviceType === 'mobile' ? GrowthBookFeature.VIP_M : GrowthBookFeature.VIP_PC,
        ExperimentVariant.VARIANT_A
    );
    const isVotRedesign =
        variant === ExperimentVariant.VARIANT_C ||
        (props.deviceType === 'desktop' && variant === ExperimentVariant.VARIANT_B);

    return (
        <BrowseLinksContent
            {...props}
            isDesktopRedesign={props.deviceType === 'desktop' && isVotRedesign}
            isModernMobileTopLocations={props.deviceType === 'mobile' && isVotRedesign}
            isVotRedesign={isVotRedesign}
        />
    );
};

const MobileSrpBrowseLinks = (props: BrowseLinksProps) => {
    const isModernMobileTopLocations =
        useFeatureValue(GrowthBookFeature.SRP_RH_MOBILE_CARD, ExperimentVariant.VARIANT_A) ===
        ExperimentVariant.VARIANT_C;

    return (
        <BrowseLinksContent {...props} isModernMobileTopLocations={isModernMobileTopLocations} />
    );
};

const BrowseLinks = (props: BrowseLinksProps) => {
    if (props.isAppView) {
        return <BrowseLinksContent {...props} />;
    }

    if (props.deviceType === 'mobile' && props.pageType === 'Homepage') {
        return <MobileHomepageBrowseLinks {...props} />;
    }

    if (props.deviceType === 'desktop' && props.pageType === 'Homepage') {
        return <DesktopHomepageBrowseLinks {...props} />;
    }

    if (
        props.deviceType === 'mobile' &&
        (props.pageType === 'ResultsSearch' || props.pageType === 'ResultsBrowse')
    ) {
        return <MobileSrpBrowseLinks {...props} />;
    }

    if (props.pageType === 'VIP') {
        return <VipBrowseLinks {...props} />;
    }

    return <BrowseLinksContent {...props} />;
};

interface BrowseLinksProps {
    browseLinks: any;
    deviceType?: string;
    isAppView?: boolean;
    isDesktopRedesign?: boolean;
    isVotRedesign?: boolean;
    pageType: string;
}

interface VotVipBrowseLinksProps {
    hasTopSearches: boolean;
    locationItems: any[];
    modernTopLocations?: boolean;
    renderTopSearchContent: () => React.ReactNode;
    showLocation: () => void;
    showTopSearch: () => void;
    showTopLocations: boolean;
}

export default BrowseLinks;
